export interface ThreadableMessage {
  id: string;
  replyToId?: string | null;
  createdAt: string;
  [key: string]: any;
}

export interface MessageWithReplies<T extends ThreadableMessage> extends ThreadableMessage {
  replies: T[];
  isOrphanReply?: boolean;
}

export interface MessageTree<T extends ThreadableMessage> {
  roots: MessageWithReplies<T>[];
  replyCount: number;
}

export function buildMessageTree<T extends ThreadableMessage>(messages: T[]): MessageTree<T> {
  const messageMap = new Map<string, T>();
  const repliesMap = new Map<string, T[]>();
  const rootMessages: T[] = [];
  const orphanReplies: T[] = [];
  let replyCount = 0;

  for (const msg of messages) {
    messageMap.set(msg.id, msg);
  }

  for (const msg of messages) {
    const parentId = msg.replyToId;
    
    if (!parentId) {
      rootMessages.push(msg);
    } else {
      replyCount++;
      const parentExists = messageMap.has(parentId);
      
      if (parentExists) {
        if (!repliesMap.has(parentId)) {
          repliesMap.set(parentId, []);
        }
        repliesMap.get(parentId)!.push(msg);
      } else {
        orphanReplies.push(msg);
      }
    }
  }

  const sortByCreatedAt = (a: T, b: T) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

  rootMessages.sort(sortByCreatedAt);

  Array.from(repliesMap.values()).forEach(replies => {
    replies.sort(sortByCreatedAt);
  });

  orphanReplies.sort(sortByCreatedAt);

  const roots: MessageWithReplies<T>[] = [];

  for (const root of rootMessages) {
    const replies = repliesMap.get(root.id) || [];
    roots.push({
      ...root,
      replies,
      isOrphanReply: false,
    });
  }

  for (const orphan of orphanReplies) {
    roots.push({
      ...orphan,
      replies: [],
      isOrphanReply: true,
    });
  }

  roots.sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    return aTime - bTime;
  });

  return { roots, replyCount };
}

export function getThreadParentIds<T extends ThreadableMessage>(messages: T[]): Set<string> {
  const parentIds = new Set<string>();
  for (const msg of messages) {
    if (msg.replyToId) {
      parentIds.add(msg.replyToId);
    }
  }
  return parentIds;
}

export function hasUnreadReplies<T extends ThreadableMessage>(
  parentId: string,
  messages: T[],
  lastReadMessageId: string | null
): boolean {
  if (!lastReadMessageId) return true;
  
  const replies = messages.filter(m => m.replyToId === parentId);
  if (replies.length === 0) return false;
  
  for (const reply of replies) {
    if (reply.id > lastReadMessageId) {
      return true;
    }
  }
  return false;
}
